import React from 'react';
import { useAppSelector } from 'store/hooks';
import { Perk } from 'components/loyalty/models/PerkModel';
import { PerkTemplate, PerkTemplateMode } from 'components/perks/templates/models/PerkTemplate';
import { SplitButtonOption } from 'ui/SplitButton';

export const usePerkTemplatesModals = () => {
    const perks = useAppSelector(state => state.perks.perks);
    const hasPerks = perks.length > 0;
    const [createModalOpen, setCreateModalOpen] = React.useState(false);
    const [perkSelectionOpen, setPerkSelectionOpen] = React.useState(false);
    const [choiceModalOpen, setChoiceModalOpen] = React.useState(false);
    const [templateMode, setTemplateMode] = React.useState<PerkTemplateMode>({ type: 'create-perk' });
    const [duplicatePerk, setDuplicatePerk] = React.useState<Perk | null>(null);
    const [templateToDelete, setTemplateToDelete] = React.useState<PerkTemplate | null>(null);

    const handleStartFromScratch = React.useCallback(() => {
        setChoiceModalOpen(false);
        setDuplicatePerk(null);
        setTemplateMode({ type: 'create-template' });
        setCreateModalOpen(true);
    }, []);

    const handleFromExistingPerk = React.useCallback(() => {
        setChoiceModalOpen(false);
        setPerkSelectionOpen(true);
    }, []);

    const handleEmptyStateCreateClick = React.useCallback(() => {
        if (hasPerks) {
            setChoiceModalOpen(true);
        } else {
            handleStartFromScratch();
        }
    }, [hasPerks, handleStartFromScratch]);

    const handleCloseChoiceModal = React.useCallback(() => {
        setChoiceModalOpen(false);
    }, []);

    const handlePerkSelected = React.useCallback((perk: Perk) => {
        setPerkSelectionOpen(false);
        setDuplicatePerk(perk);
        setTemplateMode({ type: 'create-template' });
        setCreateModalOpen(true);
    }, []);

    const handleEditTemplate = React.useCallback((template: PerkTemplate) => {
        setDuplicatePerk(null);
        setTemplateMode({ type: 'edit-template', template });
        setCreateModalOpen(true);
    }, []);

    const handleViewTemplate = React.useCallback((template: PerkTemplate) => {
        setDuplicatePerk(null);
        setTemplateMode({ type: 'view-template', template });
        setCreateModalOpen(true);
    }, []);

    const [closeConfirmOpen, setCloseConfirmOpen] = React.useState(false);

    const handleRequestClose = React.useCallback(() => {
        if (templateMode.type === 'view-template') {
            setCreateModalOpen(false);
        } else {
            setCloseConfirmOpen(true);
        }
    }, [templateMode]);

    const handleConfirmClose = React.useCallback(() => {
        setCloseConfirmOpen(false);
        setCreateModalOpen(false);
    }, []);

    const handleDismissClose = React.useCallback(() => {
        setCloseConfirmOpen(false);
    }, []);

    const handleCloseCreateModal = React.useCallback(() => {
        setCreateModalOpen(false);
    }, []);

    const handleTemplateSubmit = React.useCallback(() => {
        setCreateModalOpen(false);
    }, []);

    const handleDeleteClick = React.useCallback((template: PerkTemplate) => {
        setTemplateToDelete(template);
    }, []);

    const handleDeleteClose = React.useCallback(() => {
        setTemplateToDelete(null);
    }, []);

    const handleClosePerkSelection = React.useCallback(() => {
        setPerkSelectionOpen(false);
    }, []);

    const splitButtonOptions: SplitButtonOption[] = React.useMemo(
        () => [
            { id: 'from-scratch', label: 'Create from scratch', onClick: handleStartFromScratch },
            { id: 'from-perk', label: 'Create from existing perk', onClick: handleFromExistingPerk }
        ],
        [handleStartFromScratch, handleFromExistingPerk]
    );

    return {
        hasPerks,
        createModalOpen,
        perkSelectionOpen,
        choiceModalOpen,
        templateMode,
        duplicatePerk,
        templateToDelete,
        splitButtonOptions,
        closeConfirmOpen,
        handleStartFromScratch,
        handleFromExistingPerk,
        handleEmptyStateCreateClick,
        handleCloseChoiceModal,
        handlePerkSelected,
        handleEditTemplate,
        handleViewTemplate,
        handleRequestClose,
        handleConfirmClose,
        handleDismissClose,
        handleCloseCreateModal,
        handleTemplateSubmit,
        handleDeleteClick,
        handleDeleteClose,
        handleClosePerkSelection
    };
};
